import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90',
        outline:
          'border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        ghost: '[a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 [a&]:hover:underline',

        // ENG-1458: local extension. The four upstream variants map onto
        // `primary`/`secondary`/`destructive` only, but this app's token set
        // also defines `info`, `success` and `warning` (src/index.css
        // `@theme inline`) and uses them for status throughout. Without these
        // every status badge falls back to `className="bg-info/10 text-info"`
        // at the call site, which is exactly the drift ENG-1458 exists to
        // remove — five separate hand-rolled badges, no two alike.
        //
        // RE-SYNC: `pnpm dlx shadcn@latest add badge` will overwrite this file
        // and drop these three, the `destructive-subtle` and `muted` variants
        // below, and the whole `size` axis. Re-apply all of them, or the call sites lose
        // their tone and their type size silently — an unknown variant renders
        // as `default`, and an unknown axis is dropped without a type error at
        // the call site only because `VariantProps` regenerates with it gone.
        info: 'bg-info/10 text-info [a&]:hover:bg-info/20',
        success: 'bg-success/10 text-success [a&]:hover:bg-success/20',
        // `text-warning-strong`, not `text-warning`. `--warning` is a fill; as
        // text on the `/10` tint it scores 1.91 against WCAG AA's 4.5. `info`
        // and `success` need no such pair (5.26 and 5.20), which is exactly why
        // warning is easy to get wrong by copying the line above it. Shipping
        // the bug in the primitive would make every `<Badge variant="warning">`
        // inherit it.
        //
        // ENG-1742 UPDATE — the 5.26 / 5.20 above are the numbers ENG-1734/1735
        // later disproved: they are `--info` / `--success` measured OPAQUE ON
        // WHITE, the one surface text on its own tint never sits on. On the
        // hardest ground each theme paints (`bg-muted`/`bg-accent`, light 0.97
        // and `.dark` 0.269) the same tokens read 4.19 / 4.16 and 3.59 / 3.69
        // against AA's 4.5. `src/lib/statusTone.ts` `TONE_BADGE` — the shared
        // status chip, the same `/10` recipe — moved all four tones onto
        // `-strong` for exactly this reason. These two lines are the last
        // holdout and are a real contrast failure, left alone here only
        // because swapping them is a visible change to every existing
        // `<Badge variant="info">` / `"success"` and belongs in its own ticket
        // with the rest of the ENG-1461 sweep. Do NOT copy them as a pattern —
        // copy `destructive-subtle` below.
        warning: 'bg-warning/10 text-warning-strong [a&]:hover:bg-warning/20',

        // ENG-1742: local extension. Upstream's `destructive` is a SOLID fill
        // (`bg-destructive text-white`), so the ~66 hand-built tinted red pills
        // the `khorium/no-primitive-recipe` sweep found had no variant to land
        // on — `bg-destructive/10 text-destructive` at the call site was the
        // only spelling available, and it is also a contrast failure (3.99 on
        // its own `/10` tint against AA's 4.5). This is the tinted rung, and it
        // carries `text-destructive-strong` per the ENG-1734/1735 rule: on a
        // tint the TEXT takes the `-strong` sibling and only fills, borders,
        // rings and dots stay on the base token.
        //
        // Named `destructive-subtle`, not `danger` or `error`: ENG-1744 closed
        // the tone vocabulary at success/info/warning/destructive
        // (`src/lib/statusTone.ts`), and a fifth word for red here would
        // re-split the vocabulary that ticket just unified. `destructive` alone
        // is taken by the solid, so the tinted rung is the tone plus a
        // qualifier. Hyphenated cva values are already precedent here —
        // `input-group.tsx` ships `inline-start` and `icon-xs`.
        'destructive-subtle':
          'bg-destructive/10 text-destructive-strong [a&]:hover:bg-destructive/20',

        // ENG-1742: local extension. The NEUTRAL rung, and the one the
        // adoption sweep needed most — of the 39 pills
        // `khorium/no-primitive-recipe` reported, the single commonest
        // spelling was `bg-muted text-muted-foreground`, a quiet metadata chip
        // (a role, an attempt number, a "3 of 12"). `secondary` is the wrong
        // home for those: `--secondary` and `--muted` are the same value in all
        // three themes, but `--secondary-foreground` is 0.205 where
        // `--muted-foreground` is 0.54, so routing them through `secondary`
        // makes every de-emphasised chip in the app read as near-black.
        //
        // This is not a new colour. `Tone` in `src/lib/statusTone.ts` (ENG-1744)
        // already closes the vocabulary at success/info/warning/destructive/
        // muted/diagnosis, and `TONE_BADGE.muted` is this exact recipe. The
        // one deliberate difference is the border: `TONE_BADGE` gives every
        // tone a `border`, while every tinted variant in THIS file has none,
        // and that file's own note records the border measuring ~1.4:1 over its
        // fill in every theme — trim, not signal. Consistency inside the
        // primitive wins.
        //
        // `text-muted-foreground` needs no `-strong` sibling: ENG-1734 retuned
        // `--muted-foreground` itself (0.556 -> 0.54) instead of adding one.
        muted: 'bg-muted text-muted-foreground [a&]:hover:bg-muted/80',
      },

      // ENG-1742: local extension, a SECOND axis — see the RE-SYNC note above,
      // which covers this block too.
      //
      // The app's dense panels hand-build small pills at `text-2xs` and
      // `text-3xs`; `<Badge>` had one rung (the base `text-xs`), so those sites
      // could not compose it without overriding the primitive's own type size.
      //
      // Only the ONE size-bearing class that differs between rungs moves here.
      // `px-2 py-0.5` and `[&>svg]:size-3` deliberately stay in the base string:
      // the approved rungs are a type-scale step and nothing else, and moving
      // the padding in would leave `sm` and `xs` with no padding at all.
      // `default` is therefore byte-for-byte what the base used to render.
      size: {
        default: 'text-xs',
        sm: 'text-2xs',
        xs: 'text-3xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-size={size}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
